import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: { bg: "#fff4d6", fg: "#7a5a0a" },
  confirmed: { bg: "#dff0d8", fg: "#2f5d2f" },
  declined: { bg: "#f5d9d8", fg: "#7d2a28" },
  completed: { bg: "#e0e7ef", fg: "#2c3e50" },
};

export default function AdminPanel() {
  const nav = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("neurotheos_token") : null;
  const email = typeof window !== "undefined" ? localStorage.getItem("neurotheos_email") : null;

  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, r, s] = await Promise.all([
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/reviews/admin`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setBookings(b.data); setReviews(r.data); setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401) { localStorage.clear(); nav("/admin/login"); }
    } finally { setLoading(false); }
  }, [token, nav]); // eslint-disable-line

  useEffect(() => {
    if (!token) { nav("/admin/login"); return; }
    load();
  }, [token, load, nav]);

  const updateBooking = async (id, status_value) => {
    await axios.patch(`${API}/bookings/${id}?status_value=${status_value}`, null, { headers });
    load();
  };
  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    await axios.delete(`${API}/bookings/${id}`, { headers });
    load();
  };
  const toggleReview = async (id, approved) => {
    await axios.patch(`${API}/reviews/${id}?approved=${approved}`, null, { headers });
    load();
  };
  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await axios.delete(`${API}/reviews/${id}`, { headers });
    load();
  };

  const logout = () => { localStorage.clear(); nav("/admin/login"); };

  if (!token) return null;

  return (
    <div className="min-h-screen" style={{ background: "#fbf8f1" }} data-testid="admin-panel-page">
      <header className="border-b" style={{ borderColor: "var(--line)", background: "#fbf8f1" }}>
        <div className="container-x flex items-center justify-between" style={{ height: 76 }}>
          <div>
            <div className="serif" style={{ fontSize: 24 }}>Brain Trainer · admin</div>
            <div className="text-xs" style={{ color: "var(--ink-muted)" }}>{email}</div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="nav-link text-sm">View site</a>
            <button onClick={logout} className="btn-pill btn-outline" style={{ padding: "10px 22px" }} data-testid="admin-logout">Sign out</button>
          </div>
        </div>
      </header>

      <main className="container-x py-10">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="admin-stats">
            {[
              ["Total Bookings", stats.total_bookings],
              ["Pending", stats.pending_bookings],
              ["Confirmed", stats.confirmed_bookings],
              ["Total Reviews", stats.total_reviews],
              ["Approved Reviews", stats.approved_reviews],
            ].map(([l, v]) => (
              <div key={l} className="rounded-xl p-5" style={{ background: "#fff", border: "1px solid var(--line)" }}>
                <div className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-muted)" }}>{l}</div>
                <div className="serif mt-1" style={{ fontSize: 32 }}>{v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[["bookings", `Bookings (${bookings.length})`], ["reviews", `Reviews (${reviews.length})`]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`tab-${k}`}
              className="px-5 py-2 rounded-full text-sm"
              style={{
                background: tab === k ? "#0e0e0e" : "transparent",
                color: tab === k ? "#fbf8f1" : "#1a1a1a",
                border: "1px solid " + (tab === k ? "#0e0e0e" : "#d3c8aa"),
              }}
            >{l}</button>
          ))}
          <button onClick={load} className="ml-auto text-sm nav-link" data-testid="admin-refresh">Refresh</button>
        </div>

        {loading && <div data-testid="admin-loading">Loading...</div>}

        {tab === "bookings" && !loading && (
          <div className="space-y-3" data-testid="bookings-list">
            {bookings.length === 0 && <p style={{ color: "var(--ink-muted)" }}>No bookings yet.</p>}
            {bookings.map((b) => {
              const c = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
              return (
                <div key={b.id} className="rounded-xl p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center" style={{ background: "#fff", border: "1px solid var(--line)" }} data-testid={`booking-row-${b.id}`}>
                  <div className="md:col-span-3">
                    <div className="serif" style={{ fontSize: 20 }}>{b.name}</div>
                    <div className="text-xs" style={{ color: "var(--ink-muted)" }}>{b.contact_method}: {b.contact}</div>
                  </div>
                  <div className="md:col-span-3 text-sm">
                    <div>{b.date} · {b.time}</div>
                    <div className="text-xs mt-1" style={{ color: "var(--ink-muted)" }}>{b.service}</div>
                  </div>
                  <div className="md:col-span-3 text-sm" style={{ color: "var(--ink-soft)" }}>{b.message || "—"}</div>
                  <div className="md:col-span-1">
                    <span className="px-3 py-1 rounded-full text-xs uppercase tracking-widest" style={{ background: c.bg, color: c.fg }}>{b.status}</span>
                  </div>
                  <div className="md:col-span-2 flex gap-2 flex-wrap justify-end">
                    <select value={b.status} onChange={(e) => updateBooking(b.id, e.target.value)} data-testid={`booking-status-${b.id}`} className="text-xs px-2 py-1 rounded border" style={{ borderColor: "#d3c8aa" }}>
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="declined">Declined</option>
                      <option value="completed">Completed</option>
                    </select>
                    <button onClick={() => deleteBooking(b.id)} className="text-xs px-3 py-1 rounded" style={{ color: "#7d2a28" }} data-testid={`booking-delete-${b.id}`}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "reviews" && !loading && (
          <div className="space-y-3" data-testid="reviews-list">
            {reviews.length === 0 && <p style={{ color: "var(--ink-muted)" }}>No reviews yet.</p>}
            {reviews.map((r) => (
              <div key={r.id} className="rounded-xl p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center" style={{ background: "#fff", border: "1px solid var(--line)" }} data-testid={`review-row-${r.id}`}>
                <div className="md:col-span-2 text-xs uppercase tracking-widest" style={{ color: "var(--olive-deep)" }}>{r.review_type}</div>
                <div className="md:col-span-6 serif italic" style={{ fontSize: 16 }}>“{r.content}”</div>
                <div className="md:col-span-2 text-xs" style={{ color: "var(--ink-muted)" }}>{r.profession || "—"} {r.age && `· ${r.age}`}</div>
                <div className="md:col-span-2 flex gap-2 justify-end">
                  <button
                    onClick={() => toggleReview(r.id, !r.approved)}
                    data-testid={`review-toggle-${r.id}`}
                    className="text-xs px-3 py-1 rounded-full"
                    style={{ background: r.approved ? "#dff0d8" : "#f4ecd8", color: r.approved ? "#2f5d2f" : "#7a5a0a" }}
                  >{r.approved ? "Approved" : "Approve"}</button>
                  <button onClick={() => deleteReview(r.id)} className="text-xs px-3 py-1 rounded" style={{ color: "#7d2a28" }} data-testid={`review-delete-${r.id}`}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
